import { useReactToPrint } from 'react-to-print';
import { useRef } from 'react';

async function compressImagesInContainer(container, maxWidth = 900, quality = 0.72) {
  const imgs = Array.from(container.querySelectorAll('img'));
  const origSrcs = new Map();

  await Promise.all(imgs.map(async (img) => {
    const src = img.src;
    if (!src || src.startsWith('data:')) return;
    origSrcs.set(img, src);
    try {
      const resp = await fetch(src);
      const blob = await resp.blob();
      const bitmap = await createImageBitmap(blob);
      const scale = Math.min(1, maxWidth / bitmap.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      img.src = canvas.toDataURL('image/jpeg', quality);
    } catch {
      origSrcs.delete(img);
    }
  }));

  return () => origSrcs.forEach((src, img) => { img.src = src; });
}

/**
 * Tikab standard print hook — wraps react-to-print with A4 settings.
 * @param {React.RefObject} contentRef - ref on the element to print
 * @param {object} [options]
 * @param {string} [options.title='Dokument'] - filename shown in browser's Save as PDF dialog
 * @param {string} [options.margin='15mm'] - @page margin (use '0' for full-bleed layouts)
 * @param {boolean} [options.compressImages=false] - compress images before printing to reduce PDF size
 */
export function usePrintDocument(contentRef, options = {}) {
  const { title = 'Dokument', margin = '15mm', compressImages = false } = options;
  const restoreRef = useRef(null);

  return useReactToPrint({
    contentRef,
    documentTitle: title,
    pageStyle: `
      @page { size: A4 portrait; margin: ${margin}; }
      html, body {
        background: #ffffff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    `,
    ...(compressImages && {
      onBeforePrint: async () => {
        if (contentRef.current) {
          restoreRef.current = await compressImagesInContainer(contentRef.current);
        }
      },
      onAfterPrint: () => {
        restoreRef.current?.();
        restoreRef.current = null;
      },
    }),
  });
}
